import { create } from 'zustand';
import * as themesApi from '@/services/themes';
import useColorModeStore, { applySurfaceTokens } from '@/store/colorModeStore';

/**
 * Default theme (fallback when API is unavailable)
 * Colors are in HSL format (hue saturation% lightness%)
 */
const DEFAULT_THEME = {
  id: 1,
  name: 'سبز (پیش‌فرض)',
  isDefault: true,
  isActive: true,
  colors: {
    background: '0 0% 100%',
    foreground: '240 10% 3.9%',
    muted: '240 4.8% 95.9%',
    'muted-foreground': '240 3.8% 46.1%',
    popover: '0 0% 100%',
    'popover-foreground': '240 10% 3.9%',
    card: '0 0% 100%',
    'card-foreground': '240 10% 3.9%',
    border: '240 5.9% 90%',
    input: '240 5.9% 90%',
    primary: '162 77% 17%',
    'primary-foreground': '0 0% 100%',
    secondary: '41 65% 54%',
    'secondary-foreground': '0 0% 100%',
    accent: '240 4.8% 95.9%',
    'accent-foreground': '240 5.9% 10%',
    success: '145 100% 39%',
    'success-foreground': '160 9% 6%',
    warning: '353 100% 42%',
    'warning-foreground': '0 0% 100%',
    destructive: '0 84.2% 60.2%',
    'destructive-foreground': '0 0% 98%',
    brown: '25 50% 40%',
    'brown-foreground': '0 0% 98%',
    ring: '240 5% 64.9%',
    radius: '0.75rem',
  },
  gradients: {
    body: 'linear-gradient(270deg, rgba(5, 156, 110, 0.77) -15.61%, #1B6650 51.49%)',
    sidebar: 'linear-gradient(0deg, #0A4D39 36.73%, rgba(10, 77, 57, 0.85) 93.78%)',
  },
};

const CACHED_THEME_KEY = 'cached-theme-data';

/**
 * Load cached theme data from localStorage
 */
const loadCachedTheme = () => {
  try {
    const cached = localStorage.getItem(CACHED_THEME_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
};

/**
 * Save theme data to localStorage for instant loading
 */
const saveCachedTheme = (theme) => {
  try {
    if (theme) {
      localStorage.setItem(CACHED_THEME_KEY, JSON.stringify({
        id: theme.id,
        name: theme.name,
        colors: theme.colors,
        gradients: theme.gradients,
        isDefault: theme.isDefault,
      }));
    }
  } catch {}
};

/**
 * Theme Store
 * Manages system-wide theme (admin controlled)
 * - All users see the same active theme set by admin
 * - Theme is cached in localStorage for instant loading
 */
const useThemeStore = create((set, get) => ({
  // All themes from API (admin only)
  themes: [],
  // Active theme (for all users) - ensure it's always a valid theme
  activeTheme: (() => {
    const cached = loadCachedTheme();
    // Validate cached theme has required properties
    if (cached && cached.colors && typeof cached.colors === 'object') {
      return cached;
    }
    return DEFAULT_THEME;
  })(),
  isLoading: false,
  error: null,

  /**
   * Fetch the active theme from API (PUBLIC - for all users)
   * Called on app startup
   */
  fetchActiveTheme: async () => {
    set({ isLoading: true, error: null });
    try {
      const theme = await themesApi.getActiveTheme();
      // Validate theme before using it
      if (theme && theme.colors && typeof theme.colors === 'object') {
        set({ activeTheme: theme, isLoading: false });
        // Cache for instant loading on next visit
        saveCachedTheme(theme);
        return theme;
      } else {
        throw new Error('Invalid theme received from API');
      }
    } catch (error) {
      console.error('Error fetching active theme:', error);
      // Use cached theme if API fails
      const cached = loadCachedTheme();
      if (cached && cached.colors && typeof cached.colors === 'object') {
        set({ activeTheme: cached, isLoading: false });
        return cached;
      }
      // Always fallback to DEFAULT_THEME
      set({ activeTheme: DEFAULT_THEME, isLoading: false });
      saveCachedTheme(DEFAULT_THEME);
      return DEFAULT_THEME;
    }
  },

  /**
   * Fetch all themes from API (ADMIN only)
   */
  fetchAllThemes: async () => {
    set({ isLoading: true, error: null });
    try {
      const themes = await themesApi.getAllThemes();
      set({ themes, isLoading: false });
      return themes;
    } catch (error) {
      console.error('Error fetching themes:', error);
      set({ error: error.message, isLoading: false });
      return [];
    }
  },

  /**
   * Get the active theme - always returns a valid theme
   */
  getActiveTheme: () => {
    const activeTheme = get().activeTheme;
    // Validate theme has required properties
    if (activeTheme && activeTheme.colors && typeof activeTheme.colors === 'object') {
      return activeTheme;
    }
    return DEFAULT_THEME;
  },

  /**
   * Activate a theme (ADMIN only)
   * This sets the theme for ALL users
   */
  activateTheme: async (themeId) => {
    try {
      const result = await themesApi.activateTheme(themeId);
      const theme = result.theme;
      set({ activeTheme: theme });
      saveCachedTheme(theme);
      
      // Refresh themes list to update isActive flags
      await get().fetchAllThemes();
      
      return theme;
    } catch (error) {
      console.error('Error activating theme:', error);
      throw error;
    }
  },

  /**
   * Create a new theme (ADMIN only)
   */
  createTheme: async (themeData) => {
    try {
      const theme = await themesApi.createTheme(themeData);
      // Refresh themes list
      await get().fetchAllThemes();
      return theme;
    } catch (error) {
      console.error('Error creating theme:', error);
      throw error;
    }
  },

  /**
   * Update a theme (ADMIN only)
   */
  updateTheme: async (themeId, themeData) => {
    try {
      const theme = await themesApi.updateTheme(themeId, themeData);
      // Refresh themes list
      await get().fetchAllThemes();
      
      // If updated theme is active, update activeTheme too
      const { activeTheme } = get();
      if (activeTheme && activeTheme.id === themeId) {
        set({ activeTheme: theme });
        saveCachedTheme(theme);
      }
      
      return theme;
    } catch (error) {
      console.error('Error updating theme:', error);
      throw error;
    }
  },

  /**
   * Delete a theme (ADMIN only)
   */
  deleteTheme: async (themeId) => {
    try {
      await themesApi.deleteTheme(themeId);
      // Refresh themes list
      await get().fetchAllThemes();
      return true;
    } catch (error) {
      console.error('Error deleting theme:', error);
      throw error;
    }
  },

  /**
   * Apply preview colors temporarily (for theme editor)
   */
  applyPreviewTheme: (colors, gradients) => {
    const root = document.documentElement;
    Object.entries(colors).forEach(([key, value]) => {
      root.style.setProperty(`--${key}`, value);
    });
    if (gradients) {
      root.style.setProperty('--gradient-body', gradients.body);
      root.style.setProperty('--gradient-sidebar', gradients.sidebar);
    }
    // Keep dark surface overlay when user is in dark mode
    const resolved = useColorModeStore.getState().resolved;
    applySurfaceTokens(resolved, { colors, gradients });
  },

  /**
   * Revert to active theme (cancel preview)
   */
  revertToActiveTheme: () => {
    const { activeTheme } = get();
    if (activeTheme) {
      get().applyPreviewTheme(activeTheme.colors, activeTheme.gradients);
    }
  },
}));

export default useThemeStore;
export { DEFAULT_THEME };
